import {
  CustomParameters,
  PaywallApi,
  PaywallResult,
} from '@accedo/one-plugin-sdk';
import { t } from 'i18next';
import React, {
  FC,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { purchaseService } from '#/client';
import { useConfig } from '#/hooks/useConfig';
import { useWaitForStateCondition } from '#/hooks/useWaitForStateCondition';
import { CloseHandler, ShowPaywallParams } from '#/plugins/paywall/models';
import { getPaywallApiFromRef } from '#/plugins/paywall/utils';
import {
  DeferredPromise,
  getDeferredPromise,
  ignoreError,
  noop,
  noopPromise,
} from '#/service/utils';
import { authActions, useAuthentication } from '#/store/auth';
import { generalModalStoreActions } from '#/store/modals';

export interface PaywallError {
  title: string;
  message: string;
}

interface PaywallContextValue {
  api?: PaywallApi;
  apiRef?: MutableRefObject<PaywallApi | undefined>;
  isShowing?: boolean;
  showPaywall: (params?: ShowPaywallParams) => Promise<PaywallResult>;
  setApi: (instance: PaywallApi) => void;
  closePaywall: () => void;
  setError: (error: PaywallError | undefined) => void;
  error?: PaywallError;
  deferredPromise?: DeferredPromise<PaywallResult>;
  customParameters?: CustomParameters;
  closeHandler?: CloseHandler;
  productIds?: string[];
}

export const defaultPaywallContext: PaywallContextValue = {
  setApi: noop,
  closePaywall: noop,
  setError: noop,
  showPaywall: noopPromise,
};

// Intended to be private, only exported for test
export const PaywallContext = React.createContext<PaywallContextValue>(
  defaultPaywallContext
);

export interface PaywallProviderProps {
  api?: PaywallApi;
  isShowing?: boolean;
  closeHandler?: CloseHandler;

  // TODO: Verify the flow for these, they probably shouldn't be in here
  customParameters?: CustomParameters;
  error?: PaywallError;
}

export const PaywallProvider: FC<PaywallProviderProps> = (props) => {
  const { children } = props;

  const {
    isInitialised: isAuthInitialised,
    isLoggedIn,
    isEntitled,
    purchaseEntitlements,
  } = useAuthentication();

  const config = useConfig();
  const isInAppEnabled = config.features.purchase?.inApp?.enabled || false;

  const apiRef = useRef<PaywallApi>();

  const [api, setApi] = useState<PaywallApi | undefined>(props.api);
  const [isShowing, setIsShowing] = useState<boolean>(!!props.isShowing);
  const [error, setError] = useState<PaywallError | undefined>(props.error);

  const [productIds, setProductIds] = useState<string[] | undefined>();
  const waitForProductIds = useWaitForStateCondition({
    state: productIds,
    interval: 100,
    timeout: 10_000,
    rejectMessage: 'Waiting for product IDs timed out',
  });

  const [closeHandler, setCloseHandler] = useState<CloseHandler | undefined>(
    props.closeHandler
  );

  const [customParameters, setCustomParameters] = useState<
    CustomParameters | undefined
  >(props.customParameters);

  const [deferredPromise, setDeferredPromise] =
    useState<DeferredPromise<PaywallResult>>();

  const showPaywall = useCallback(
    async (params: ShowPaywallParams): Promise<PaywallResult> => {
      // if the user is not logged in, redirect to the login modal instead of paywall
      if (!isLoggedIn) {
        authActions.showLogin();

        return Promise.resolve(PaywallResult.Aborted);
      }

      // if the user already has any subscription, don't show paywall
      if (isEntitled) {
        generalModalStoreActions.showModal({
          title: t('iap.alreadysubscribed.title'),
          message: t('iap.alreadysubscribed.message'),
          buttonLabel: t('button.ok'),
        });

        return Promise.resolve(PaywallResult.Aborted);
      }

      // we have to make sure that we have the product ids fetched and ready before showing the
      // Paywall, otherwise it will show all the Plans, unfiltered.
      // TODO#valtio: there should be a better way to do this after migrating PaywallContext to valtio
      await waitForProductIds();

      const _deferred = getDeferredPromise<PaywallResult>();

      setIsShowing(true);
      setDeferredPromise(_deferred);

      if (params?.closeHandler) {
        setCloseHandler(params?.closeHandler);
      }

      if (params?.customParameters) {
        setCustomParameters(params?.customParameters);
      }

      return _deferred.promise;
    },
    [isEntitled, isLoggedIn, waitForProductIds]
  );

  const closePaywall = useCallback((): void => {
    // just in case the promise is still pending, reject it before closing
    deferredPromise?.reject(PaywallResult.Aborted);

    setIsShowing(false);
    setDeferredPromise(undefined);
    setCloseHandler(undefined);
    setCustomParameters(undefined);
  }, [deferredPromise]);

  const apiInstance = useMemo(() => api || getPaywallApiFromRef(apiRef), [api]);

  // get purchasable products from backend
  useEffect(() => {
    if (!(isAuthInitialised && isLoggedIn && isInAppEnabled)) {
      return;
    }

    // TODO: We currently call this twice when we are doing a deeplink purchase
    // Potentially addressable when migrating paywall context to store
    purchaseService
      .getProducts(purchaseEntitlements)
      .then((res) => {
        setProductIds(res?.data?.productIds);
      })
      .catch(ignoreError);
  }, [isAuthInitialised, isInAppEnabled, isLoggedIn, purchaseEntitlements]);

  const value = useMemo((): PaywallContextValue => {
    return {
      isShowing,
      showPaywall,
      api: apiInstance,
      apiRef,
      setApi,
      customParameters,
      closeHandler,
      closePaywall,
      deferredPromise,
      error,
      setError,
      productIds,
    };
  }, [
    apiInstance,
    showPaywall,
    isShowing,
    customParameters,
    closePaywall,
    deferredPromise,
    closeHandler,
    error,
    productIds,
  ]);

  return (
    <PaywallContext.Provider value={value}>{children}</PaywallContext.Provider>
  );
};

export const usePaywall = () => React.useContext(PaywallContext);
